import { Stack } from "@mui/material";
import { CrossRed } from "src/assets";
import { MSG_IDS } from "src/consts/msgIds";
import { useTranslation } from "react-i18next";
import Text from "src/components/Displays/Text";
import { useContext, useEffect, useState } from "react";
import BackButton from "src/components/Shared/BackButton";
import { ThemeValues } from "src/components/ThemeUsage/theme";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ThemeContext } from "src/components/ThemeUsage/ThemeUsage";
import { SMART_CHARGING_ROUTES } from "src/consts/Paths/SmartCharging";

const ConnectingError = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const themeName = useContext(ThemeContext);
  const [errorMsg, setErrorMsg] = useState("");
  const msgId = searchParams.get("msgId") ?? "";

  const handleSubmit = () => {
    navigate(`${SMART_CHARGING_ROUTES.INTRO}?theme=${themeName}`);
  };

  const handleRetry = () => {
    navigate(SMART_CHARGING_ROUTES.CONNECTING);
  };

  useEffect(() => {
    switch (msgId) {
      case MSG_IDS.smart_charging_onboarding_waiting_for_connection:
        setErrorMsg(
          "Bitte überprüfen Sie, ob die OCPP-URL korrekt in den OCPP-Einstellungen Ihrer Wallbox eingesetzt wurde.",
        );
        break;
      case MSG_IDS.smart_charging_onboarding_waiting_for_boot_notification:
        setErrorMsg("Bitte starten Sie Ihre Wallbox neu.");
        break;
      case MSG_IDS.smart_charging_onboarding_missing_data:
        setErrorMsg(
          "Bitte führen Sie ein Firmware Update Ihrer Wallbox durch und versuchen Sie es erneut.",
        );
        break;
      case MSG_IDS.smart_charging_onboarding_duplicate_charge_point_id:
        setErrorMsg(
          "Bitte konfigurieren Sie eine eindeutige ChargePointId in den OCPP-Einstellungen Ihrer Wallbox. Empfehlung: Nutzen Sie die Serial Nummer Ihrer Wallbox.",
        );
        break;
      default:
        setErrorMsg(t(`page.errorPage.sub_title_default`));
        break;
    }
  }, [t, msgId]);

  return (
    <>
      <Stack gap={5}>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <BackButton
            variant={
              themeName === ThemeValues.lichtblick ||
              themeName === ThemeValues.SMA
                ? "dark"
                : "light"
            }
            onClick={() => navigate(-1)}
            sx={(theme) => ({
              backgroundColor: theme.palette.background.default,
              "&:hover": {
                backgroundColor: theme.palette.background.default,
              },
              display: { xs: "none", sm: "flex" },
            })}
          />
        </Stack>

        <Stack gap={5}>
          <CrossRed />
          <Text
            text={t("page.wallboxConnectingError.pageTitle")}
            sx={(theme) => ({
              fontWeight: 500,
              fontSize: "32px",
              lineHeight: "40px",
              fontFamily: "officina",
              color: "#A1241B",
            })}
          />
        </Stack>

        <Stack gap={5}>
          <Text
            text={t("page.wallboxConnectingError.pageSubTitle")}
            sx={(theme) => ({
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "20px",
              fontFamily: "Inter",
              color: theme.palette.mainFont.primary,
            })}
          />

          <Stack
            sx={{
              padding: "10px",
              borderRadius: "10px",
              bgcolor: "#F9F9F9",
              border: "1px solid rgba(0, 0, 0, 0.40)",
            }}
          >
            <Text
              text={errorMsg}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />
          </Stack>
        </Stack>

        <Stack gap={5}>
          <Stack>
            <Text
              text={t("page.wallboxConnectingError.point1Title")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: 700,
                lineHeight: "24px",
                color: theme.palette.mainFont.primary,
              })}
            />

            <Text
              text={t("page.wallboxConnectingError.point1Description")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />
          </Stack>

          <Stack>
            <Text
              text={t("page.wallboxConnectingError.point2Title")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: 700,
                lineHeight: "24px",
                color: theme.palette.mainFont.primary,
              })}
            />

            <Text
              text={t("page.wallboxConnectingError.point2Description")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />
          </Stack>

          <Stack>
            <Text
              text={t("page.wallboxConnectingError.point3Title")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: 700,
                lineHeight: "24px",
                color: theme.palette.mainFont.primary,
              })}
            />

            <Text
              text={t("page.wallboxConnectingError.point3Description")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />
          </Stack>

          <Stack>
            <Text
              text={t("page.wallboxConnectingError.point4Title")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: 700,
                lineHeight: "24px",
                color: theme.palette.mainFont.primary,
              })}
            />

            <Text
              text={t("page.wallboxConnectingError.point4Description")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />
          </Stack>

          <Stack>
            <Text
              text={t("page.wallboxConnectingError.point5Description")}
              sx={(theme) => ({
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "20px",
                fontFamily: "Inter",
                color: theme.palette.mainFont.primary,
              })}
            />
          </Stack>
        </Stack>

        <Stack gap={2} sx={{ maxWidth: { sm: "252px" } }}>
          <PrimaryButton
            sx={(theme) => ({
              color: "#fff",
              bgcolor: "#000",
              "&:hover": {
                color: "#fff",
                bgcolor: "#000",
              },
            })}
            onClick={handleSubmit}
            text={t("page.wallboxConnectingError.previousButton")}
          />

          <PrimaryButton
            onClick={handleRetry}
            text={t("page.wallboxConnectingError.retryButton")}
          />
        </Stack>
      </Stack>
    </>
  );
};

export default ConnectingError;
